/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Toolbar,
    TOOLBAR_ITEM_TYPE,
    toolbarItemDecorators,
    useGridApi,
    useModal,
} from '@veeam-vspc/components';

import type { ToolbarItems } from '@veeam-vspc/components';

import { useLang } from 'views/providers/LangProvider/hooks';
import { canRemoveResource } from '../../../../utils';
import { ResourceDialog } from '../ResourceDialog';
import addIcon from 'images/actions/add.svg';
import removeIcon from 'images/actions/remove.svg';
import editIcon from 'images/actions/edit.svg';

import type { ResellerReplicaResource } from '../../../../../../interfaces';

interface Props {
    items: ResellerReplicaResource[];
    cloudConnectAgentUids: string[];
    wanAccelerationIsEnabled: boolean;
}

export const ActionsToolbar = ({ items, cloudConnectAgentUids, wanAccelerationIsEnabled }: Props) => {
    const lang = useLang();
    const gridApi = useGridApi<ResellerReplicaResource, void, void>();

    const [quotaDialog, quotaDialogActions] = useModal<ResellerReplicaResource>({
        render: ({ deactivate, data }) => (
            <ResourceDialog
                data={data}
                existingItems={items}
                cloudConnectAgentUids={cloudConnectAgentUids}
                wanAccelerationIsEnabled={wanAccelerationIsEnabled}
                onConfirm={(newData) => {
                    if (data) {
                        items[items.findIndex(item => item.cloudConnectResourceUid === data.cloudConnectResourceUid)] = newData;
                    } else {
                        items.push(newData);
                    }
                    gridApi.reloadGrid();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const toolbarItems: ToolbarItems = [
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: addIcon,
            text: lang.ADD,
            onClick: () => {
                quotaDialogActions.activate();
            },
            decorators: [
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: editIcon,
            text: lang.EDIT,
            onClick: async() => {
                const selected = gridApi.selected[0];
                quotaDialogActions.activate(selected);
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: removeIcon,
            text: lang.REMOVE,
            onClick: async() => {
                const selected = gridApi.selected[0];
                if (
                    await canRemoveResource(
                        selected,
                        lang.CANNOT_DELETE_THE_HARDWARE_PLAN,
                        lang.THIS_ACTION_WILL_REMOVE_CLOUD_REPLICATION,
                        lang
                    )
                ) {
                    items.splice(items.findIndex(item => item.cloudConnectResourceUid === selected.cloudConnectResourceUid), 1);
                    gridApi.reloadGrid();
                }
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
    ];

    return (
        <>
            <Toolbar items={toolbarItems} />
            {quotaDialog}
        </>
    );
};
